import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../AuthProvider';
import { FaSearch, FaQuestionCircle, FaUser, FaCaretDown, FaCog, FaBell, FaSignOutAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './AboveAll.css';

const searchablePages = [
  { label: "Cars", path: "/login/cars" },
  { label: "Drivers", path: "/login/drivers" },
  { label: "Companies", path: "/login/companies" },
  { label: "Trips", path: "/login/trips" },
  { label: "New Trip", path: "/login/trips/add-new" },
  { label: "New completed trip", path: "/login/trips/add-completed" },
  { label: "Petty Cash", path: "/login/pettycash" },
  { label: "Fuel", path: "/login/fuels" },
  { label: "Garages", path: "/login/garages" },
  { label: "Labels", path: "/login/labels" },
  { label: "Billing Items", path: "/login/billing_items" },
  { label: "Trip Types", path: "/login/trip_types" },
  { label: "Taxes", path: "/login/taxes" },
  { label: "New Car", path: "/login/new_car" },
  { label: "New Driver", path: "/login/new_driver" },
  { label: "New Company", path: "/login/new_company" },
  { label: "Invoices", path: "/login/invoices" },
  { label: "Expenditures", path: "/login/expenditures" },
  { label: "Fuel Analysis", path: "/login/cars/fuel_analysis" },
  { label: "Fuel Stations", path: "/login/fuels/fuel_stations" },
  { label: "Roles", path: "/login/home/roles" },
  { label: "Employees", path: "/login/home/employees" },
];

const AboveAll = () => {
  const { vendorDetails } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('recentSearches') || '[]');
    } catch {
      return [];
    }
  });

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length > 0) {
      const results = searchablePages.filter(page =>
        page.label.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(results);
      setShowResults(true);
      setHighlightedIndex(-1);
    } else {
      setShowResults(true);
      setSearchResults([]);
      setHighlightedIndex(-1);
    }
  };

  const handleResultClick = (path, label) => {
    setShowResults(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
    setRecentSearches(prev => {
      const updated = [label, ...prev.filter(l => l !== label)].slice(0, 5);
      localStorage.setItem('recentSearches', JSON.stringify(updated));
      return updated;
    });
    navigate(path);
  };

  const handleClear = () => {
    setSearchQuery('');
    setShowResults(false);
    setSearchResults([]);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    if (!searchQuery) setShowResults(true);
  };

  const handleKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < searchResults.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : searchResults.length - 1
      );
    } else if (e.key === "Enter") {
      if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
        handleResultClick(searchResults[highlightedIndex].path, searchResults[highlightedIndex].label);
      }
    }
  };

  const handleProfileClick = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const handleProfileAction = (action) => {
    // TODO: Implement profile actions
    console.log('Profile action:', action);
    setIsProfileOpen(false);
  };

  return (
    <div className="above-all">
      {/* Left Section - Vendor Name */}
      <div className="vendor-section">
        <h1 className="vendor-name">{vendorDetails?.name || 'Vendor Name'}</h1>
        <p className="vendor-subtitle">VendorBooks</p>
      </div>

      {/* Middle Section - Search and Help */}
      <div className="middle-section">
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
            onFocus={handleInputFocus}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            onKeyDown={handleKeyDown}
          />
          {searchQuery && (
            <button className="search-clear-btn" onClick={handleClear} tabIndex={-1}>
              ×
            </button>
          )}
          {showResults && (
            <div className="search-results-dropdown">
              {searchQuery && searchResults.length > 0 ? (
                searchResults.map((result, idx) => {
                  const matchIndex = result.label.toLowerCase().indexOf(searchQuery.toLowerCase());
                  let before = result.label.slice(0, matchIndex);
                  let match = result.label.slice(matchIndex, matchIndex + searchQuery.length);
                  let after = result.label.slice(matchIndex + searchQuery.length);
                  return (
                    <div
                      key={idx}
                      className={`search-result-item${highlightedIndex === idx ? ' highlighted' : ''}`}
                      onClick={() => handleResultClick(result.path, result.label)}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                      ref={el => {
                        if (highlightedIndex === idx && el) {
                          el.scrollIntoView({ block: 'nearest' });
                        }
                      }}
                    >
                      <span>
                        {before}
                        <span className="match">{match}</span>
                        {after}
                      </span>
                    </div>
                  );
                })
              ) : !searchQuery && recentSearches.length > 0 ? (
                <>
                  <div className="search-recent-title">Recent Searches</div>
                  {recentSearches.map((label, idx) => {
                    const page = searchablePages.find(p => p.label === label);
                    if (!page) return null;
                    return (
                      <div
                        key={label}
                        className={`search-result-item${highlightedIndex === idx ? ' highlighted' : ''}`}
                        onClick={() => handleResultClick(page.path, page.label)}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                      >
                        <span>{label}</span>
                      </div>
                    );
                  })}
                </>
              ) : (
                <div className="search-no-results">No results found</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Section - Profile */}
      <div className="profile-section" ref={profileRef}>
        <div className="profile-info">
            <button className="profile-button" onClick={handleProfileClick}>
            <FaUser className="profile-icon" />
          </button>
          <div className="user-details">
            <span className="user-name">{vendorDetails?.name || 'User'}</span>
            <span className="user-email">{vendorDetails?.email || 'user@example.com'}</span>
          </div>
          
        </div>
        
        {isProfileOpen && (
          <div className={`profile-dropdown ${isProfileOpen ? 'show' : ''}`}>
            <div className="dropdown-header">
              <div className="dropdown-profile-icon">
                <FaUser />
              </div>
              <div className="dropdown-user-info">
                <span className="dropdown-username">{vendorDetails?.name || 'User'}</span>
                <span className="dropdown-email">{vendorDetails?.email || 'user@example.com'}</span>
              </div>
            </div>
            <div className="dropdown-divider" />
            <button 
              className="dropdown-item"
              onClick={() => handleProfileAction('settings')}
              style={{ '--index': 0 }}
            >
              <FaCog /> Settings
            </button>
            <button 
              className="dropdown-item"
              onClick={() => handleProfileAction('notifications')}
              style={{ '--index': 1 }}
            >
              <FaBell /> Notifications
            </button>
            <button 
              className="dropdown-item"
              onClick={() => handleProfileAction('logout')}
              style={{ '--index': 2 }}
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AboveAll; 